import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { Alert } from '../ui/Alert';
import { validateEmail } from '../../utils/validation';

export interface ContactSalesFormData {
  school_name: string;
  contact_person: string;
  email: string;
  phone: string;
  country: string;
  student_count: string;
  message: string;
}

const STUDENT_COUNT_OPTIONS = [
  { value: '1-50', label: '1–50 students' },
  { value: '51-200', label: '51–200 students' },
  { value: '201-500', label: '201–500 students' },
  { value: '501-1000', label: '501–1,000 students' },
  { value: '1000+', label: '1,000+ students' },
];

export const ContactSalesForm: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactSalesFormData>();

  const onSubmit = async () => {
    setSubmitted(true);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {submitted ? (
        <Alert variant="info">
          Coming soon — our team will enable online inquiries shortly. Thank you for your interest in
          Tamil Katral Maiyam.
        </Alert>
      ) : null}

      <Input
        label="School name"
        {...register('school_name', { required: 'School name is required' })}
        error={errors.school_name?.message}
        placeholder="Your school or organization"
        disabled={submitted}
      />

      <Input
        label="Contact person"
        {...register('contact_person', { required: 'Contact person is required' })}
        error={errors.contact_person?.message}
        placeholder="Full name"
        disabled={submitted}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Email"
          type="email"
          {...register('email', {
            required: 'Email is required',
            validate: (v) => validateEmail(v) || 'Invalid email address',
          })}
          error={errors.email?.message}
          placeholder="admin@school.edu"
          disabled={submitted}
        />
        <Input
          label="Phone"
          {...register('phone', { required: 'Phone is required' })}
          error={errors.phone?.message}
          placeholder="+1 555 000 0000"
          disabled={submitted}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Country"
          {...register('country', { required: 'Country is required' })}
          error={errors.country?.message}
          placeholder="e.g. United States"
          disabled={submitted}
        />
        <Select
          label="Student count"
          options={STUDENT_COUNT_OPTIONS}
          {...register('student_count', { required: 'Student count is required' })}
          error={errors.student_count?.message}
          disabled={submitted}
        />
      </div>

      <Textarea
        label="Message"
        rows={4}
        {...register('message', { required: 'Message is required' })}
        error={errors.message?.message}
        placeholder="Tell us about your Tamil program, grades, and goals…"
        disabled={submitted}
      />

      <Button type="submit" className="w-full sm:w-auto" isLoading={isSubmitting} disabled={submitted}>
        {submitted ? 'Submitted' : 'Send inquiry'}
      </Button>
    </form>
  );
};
